class SyncUTMsToCart {
    constructor() {
        this.utms = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
        this.targetUrlPatterns = [
            "change.js",
            "add.js",
            "change",
            "add",
            "update.js",
        ];
        this.originalSend = null;
        this.originalFetch = null;
        this.isProcessing = false;

        // Shopify Storefront API configuration
        this.storefrontApiUrl = 'https://prostandard.myshopify.com/api/2025-04/graphql.json';
        this.storefrontAccessToken = 'dd8e9c2ef1a28ca69bb3aeadfb6eeddb';

        // Target metafield keys
        this.targetMetafields = ['league', 'gender', 'department', 'team', 'capsule'];

        this.init();
    }

    init() {
        this.setCampaignUTMsToSessionStorage();
        this.setupURLChangeListener();
    }

    setCampaignUTMsToSessionStorage() {
        const storedUTMs = sessionStorage.getItem('campaignUTMs');
        if (!storedUTMs) {
            const utms = this.getUTMParams();
            if (Object.keys(utms).length > 0) {
                sessionStorage.setItem('campaignUTMs', JSON.stringify(utms));
            }
        }
    }

    getUTMParams() {
        const params = new URLSearchParams(window.location.search);
        const utms = {};
        this.utms.forEach(key => {
            if (params.has(key)) {
                utms[key] = params.get(key);
            }
        });
        return utms;
    }

    async updateCartAttributes(utms) {
        const response = await this.originalFetch('/cart/update.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ attributes: utms })
        });
        return response.json();
    }

    async syncUTMsToCart() {
        const storedUTMs = sessionStorage.getItem('campaignUTMs');
        if (storedUTMs) {
            try {
                const utms = JSON.parse(storedUTMs);
                await this.updateCartAttributes(utms);
                console.log('UTMs synced to cart', utms);
            } catch (error) {
                console.error('Error syncing UTMs to cart:', error);
            }
        }
    }

    async fetchProductsMetafields(productIds) {
        try {
            if (!productIds || productIds.length === 0) {
                return {};
            }

            const query = `
                query MyQuery($ids: [ID!]!) {
                    nodes(ids: $ids) {
                        ... on Product {
                            id
                            league: metafield(key: "league" , namespace: "custom") {
                                value
                                key
                            }
                            gender: metafield(key: "gender" , namespace: "custom") {
                                value
                                key
                            }
                            department: metafield(key: "department" , namespace: "custom") {
                                value
                                key
                            }
                            team: metafield(key: "team" , namespace: "custom") {
                                value
                                key
                            }
                            capsule: metafield(key: "capsule" , namespace: "custom") {
                                value
                                key
                            }
                        }
                    }
                }
            `;

            const variables = {
                ids: productIds.map(id => `gid://shopify/Product/${id}`)
            };

            const response = await this.originalFetch(this.storefrontApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
                },
                body: JSON.stringify({ query, variables })
            });

            if (!response.ok) {
                throw new Error(`GraphQL request failed: ${response.status}`);
            }

            const data = await response.json();

            if (data.errors) {
                throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
            }

            const productMetafieldsMap = {};

            if (data.data?.nodes) {
                data.data.nodes.forEach(node => {
                    if (node && node.id) {
                        const productId = node.id.split('/').pop();
                        const metafields = {};

                        this.targetMetafields.forEach(key => {
                            if (node[key] && node[key].value) {
                                try {
                                    let parsedValue = node[key].value;

                                    if (parsedValue.startsWith('[') && parsedValue.endsWith(']')) {
                                        const arrayValue = JSON.parse(parsedValue);
                                        parsedValue = Array.isArray(arrayValue) ? arrayValue.join(', ') : parsedValue;
                                    }

                                    metafields[key] = parsedValue;
                                } catch (parseError) {
                                    metafields[key] = node[key].value;
                                }
                            }
                        });

                        if (Object.keys(metafields).length > 0) {
                            productMetafieldsMap[productId] = metafields;
                        }
                    }
                });
            }

            return productMetafieldsMap;
        } catch (error) {
            console.error('Error fetching products metafields:', error);
            return {};
        }
    }

    async updateCartLineProperties(lineItemKey, metafields) {
        try {
            const properties = {};
            Object.keys(metafields).forEach(key => {
                properties[`_${key}`] = metafields[key];
            });

            const response = await this.originalFetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: lineItemKey,
                    properties: properties
                })
            });

            if (!response.ok) {
                throw new Error(`Cart update failed: ${response.status}`);
            }

            const result = await response.json();
            console.log('Cart line properties updated:', properties);
            return result;
        } catch (error) {
            console.error('Error updating cart line properties:', error);
            return null;
        }
    }

    async getCartData() {
        try {
            const response = await this.originalFetch('/cart.json');
            if (!response.ok) {
                throw new Error(`Cart fetch failed: ${response.status}`);
            }
            const cartData = await response.json();
            return cartData;
        } catch (error) {
            console.error('Error fetching cart data:', error);
            return null;
        }
    }

    async processCartItems() {
        if (this.isProcessing) return;
        this.isProcessing = true;

        try {
            const cartData = await this.getCartData();
            if (!cartData || !cartData.items || cartData.items.length === 0) {
                console.log('No cart items found');
                return;
            }

            console.log('Processing cart items:', cartData.items);

            const productIdsToFetch = [];
            const itemsToUpdate = [];

            for (const item of cartData.items) {
                const hasMetafieldProperties = this.targetMetafields.some(key =>
                    item.properties && item.properties[`_${key}`]
                );

                if (!hasMetafieldProperties && item.product_id) {
                    productIdsToFetch.push(item.product_id);
                    itemsToUpdate.push(item);
                }
            }

            if (productIdsToFetch.length === 0) {
                console.log('No items need metafield updates');
                return;
            }

            console.log('Fetching metafields for products:', productIdsToFetch);
            const productMetafieldsMap = await this.fetchProductsMetafields(productIdsToFetch);

            for (const item of itemsToUpdate) {
                const metafields = productMetafieldsMap[item.product_id];
                if (metafields && Object.keys(metafields).length > 0) {
                    await this.updateCartLineProperties(item.key, metafields);
                } else {
                    console.log('No target metafields found for product:', item.product_id);
                }
            }
        } catch (error) {
            console.error('Error processing cart items:', error);
        } finally {
            this.isProcessing = false;
        }
    }

    setupURLChangeListener() {
        const self = this;

        // Capture URL when XHR open() is called
        const originalOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(method, url) {
            this._url = url;
            return originalOpen.apply(this, arguments);
        };

        // Override XHR send to listen for cart-related requests
        this.originalSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.send = function() {
            this.addEventListener("load", function() {
                const isTargetRequest = self.targetUrlPatterns.some(
                    (pattern) => this._url && this._url.includes(pattern)
                );

                if (isTargetRequest) {
                    setTimeout(() => {
                        self.syncUTMsToCart();

                        if (this._url.includes('add')) {
                            setTimeout(() => {
                                self.processCartItems();
                            }, 500);
                        }
                    }, 400);
                }
            });

            return self.originalSend.apply(this, arguments);
        };

        // Backup the original fetch
        this.originalFetch = window.fetch.bind(window);

        // Override global fetch to monitor cart-related requests
        window.fetch = function(...fetchArgs) {
            const requestUrl = typeof fetchArgs[0] === 'string' ? fetchArgs[0] : fetchArgs[0]?.url || '';

            return self.originalFetch(...fetchArgs).then(async (response) => {
                if (!response.ok) {
                    return response;
                }

                try {
                    const isTargetResponse = self.targetUrlPatterns.some(
                        (pattern) => response.url.includes(pattern)
                    );

                    if (isTargetResponse) {
                        setTimeout(() => {
                            self.syncUTMsToCart();

                            if (response.url.includes('add')) {
                                setTimeout(() => {
                                    self.processCartItems();
                                }, 500);
                            }
                        }, 400);
                    }
                } catch (error) {
                    console.warn(
                        "[shop_events_listener] Error handling fetch request:",
                        error.message
                    );
                }

                return response;
            });
        };
    }
}

document.addEventListener("DOMContentLoaded", () => {
    new SyncUTMsToCart();
});